import { SERVER_OWNED_FIELDS, type PlatformConfig } from '@aurora/platform'

/**
 * Platform configuration — what the local-first engine needs to know about
 * Aurora's data.
 *
 * Two layers (docs/07-synthesis.md):
 *   substrate — threads, messages, captures, artifacts, memories: the open
 *               conversation the agent operates in
 *   spine     — skills, skillStates, cycles, weeks, days, blocks: the typed
 *               progression model the daily loop and charts run on
 */

export const AURORA_PLATFORM_CONFIG: PlatformConfig = {
  dbName: 'aurora-store',
  dbVersion: 3,

  registry: {
    children: {
      persistence: { kind: 'collection', key: 'children' },
      localFirst: true,
      dispatchTable: { table: 'children', kind: 'collection' },
    },
    // The people on the account: read-only here, edited through the auth API.
    members: {
      persistence: { kind: 'collection', key: 'members' },
    },
    threads: {
      persistence: { kind: 'collection', key: 'threads' },
      localFirst: true,
      dispatchTable: { table: 'threads', kind: 'collection' },
    },
    messages: {
      persistence: { kind: 'collection', key: 'messages' },
      localFirst: true,
      dispatchTable: { table: 'messages', kind: 'collection' },
    },
    artifacts: {
      persistence: { kind: 'collection', key: 'artifacts' },
      localFirst: true,
      dispatchTable: { table: 'artifacts', kind: 'collection' },
      unprotectedFields: SERVER_OWNED_FIELDS.artifacts,
    },
    captures: {
      persistence: { kind: 'collection', key: 'captures' },
      localFirst: true,
      dispatchTable: { table: 'captures', kind: 'collection' },
    },
    memories: { persistence: { kind: 'collection', key: 'memories' }, localFirst: true },
    schedules: { persistence: { kind: 'collection', key: 'schedules' }, localFirst: true },

    // the spine
    skills: { persistence: { kind: 'collection', key: 'skills' }, localFirst: true },
    skillStates: {
      persistence: { kind: 'collection', key: 'skillStates' },
      localFirst: true,
      dispatchTable: { table: 'skill_states', kind: 'collection' },
    },
    cycles: { persistence: { kind: 'collection', key: 'cycles' }, localFirst: true },
    weeks: { persistence: { kind: 'collection', key: 'weeks' }, localFirst: true },
    days: { persistence: { kind: 'collection', key: 'days' }, localFirst: true },
    blocks: {
      persistence: { kind: 'collection', key: 'blocks' },
      localFirst: true,
      dispatchTable: { table: 'blocks', kind: 'collection' },
    },
    /** Append-only, server-authored: where each axis was, over time. */
    levelHistory: { persistence: { kind: 'collection', key: 'levelHistory' }, localFirst: true },
    /** The check-in ritual: the parent's half is written here, the agent's by tool. */
    reviews: {
      persistence: { kind: 'collection', key: 'reviews' },
      localFirst: true,
      dispatchTable: { table: 'reviews', kind: 'collection' },
    },

    /** Server-authored: her memory of each item (a letter, a word, a card), from what she tried. */
    itemMemory: { persistence: { kind: 'collection', key: 'itemMemory' }, localFirst: true },

    account: { persistence: { kind: 'meta', key: 'account' }, hydration: { merge: 'fill' } },
    clientState: {
      persistence: { kind: 'meta', key: 'clientState' },
      dispatchTable: { table: 'client_state', kind: 'singleton' },
    },

    pending: { persistence: { kind: 'meta', key: 'pending' } },
    syncMeta: { persistence: { kind: 'meta', key: 'syncMeta' } },

    // Ephemeral view state — never persisted, never dispatched.
    ui: {},
    capture: {},
  },

  syncRegistry: {
    children: { isDelta: true, altKey: 'client_id' },
    members: { isDelta: false },
    threads: { isDelta: true, altKey: 'client_id' },
    messages: { isDelta: true, altKey: 'client_id' },
    captures: { isDelta: true, altKey: 'client_id' },
    schedules: { isDelta: true },

    /**
     * Artifact bodies can be large (a full plan, a generated activity's HTML).
     * `deep_merge` keeps a metadata-only delta from clobbering content the
     * client already holds.
     */
    artifacts: {
      isDelta: true,
      altKey: 'client_id',
      merge: { data: 'deep_merge', body: 'replace', html: 'replace' },
    },

    memories: { isDelta: true, altKey: 'client_id' },

    // the spine — mostly server-authored; the parent's thin writable surface
    // (block status, level pins, notes) is protected by the platform's pending
    // locks, which clear only when the server echoes the local value. That is
    // stronger than local_wins here: an agent revision can still legitimately
    // change these fields once the parent's own write has landed.
    skills: { isDelta: true },
    skillStates: { isDelta: true },
    cycles: { isDelta: true, altKey: 'client_id' },
    weeks: { isDelta: true },
    days: { isDelta: true },
    blocks: { isDelta: true, altKey: 'client_id' },
    levelHistory: { isDelta: true },
    reviews: { isDelta: true, altKey: 'client_id' },
    itemMemory: { isDelta: true },

    account: { kind: 'singleton' },
    clientState: {
      kind: 'singleton',
      merge: {
        theme: 'local_wins',
        lastView: 'local_wins',
        activeChildId: 'local_wins',
        activeThreadId: 'local_wins',
        notificationPrefs: 'local_wins',
      },
    },
  },

  /** Large payloads, written debounced and TTL-pruned rather than kept hot. */
  detailTables: {
    artifactContent: { keyField: 'artifact_id' },
  },

  /**
   * A capture must NEVER be lost — it is the highest-value data in the system
   * and is routinely recorded offline. Agent turns are here too: a half-sent
   * message that vanishes is worse than one that arrives late.
   */
  mustDeliverActions: new Set([
    'logVoiceCapture',
    'logTextCapture',
    'logQuickCapture',
    'logInteractiveResult',
    'generateMaterial',
    'savePhoto',
    'setBlockNote',
    'submitReview',
    'closeDay',
    'createArtifact',
    'addChild',
    'sendMessage',
    'saveArtifactEdit',
    /**
     * Playing an activity is an observation, so a result weighs the same as a
     * capture. Generation is here because a request made on a patchy
     * connection must still produce the material later rather than silently
     * evaporating while the parent waits for it.
     */
    'logInteractiveResult',
    'generateMaterial',
    /** A check-in the parent filled in must land, however patchy the evening signal. */
    'submitReview',
    'closeDay',
    /** A deck asked for, or a card a parent typed in, must reach her decks even when added offline. */
    'createDeck',
    'updateDeck',
    /** A reading-path step she started must become her row, or its progress has nowhere to land. */
    'openPathStep',
    /** A retry asked for must reach the server, or the row sits in "making" forever. */
    'retryGeneration',
  ]),

  outboxCoalesceKeys: {
    setClientPref: (a: any) => `pref:${a?.key ?? ''}`,
    updateChild: (a: any) => `child:${a?.childId ?? ''}`,
    updateThreadState: (a: any) => `thread:${a?.threadId ?? ''}`,
  },

  /**
   * When an optimistic row is rekeyed to its server id, move client-side
   * pointers with it — otherwise a note captured offline leaves the UI pointing
   * at a dead stub once it syncs.
   */
  rekeyExtra: (draft: any, oldId: string, newId: string) => {
    const ui = draft.ui ?? {}
    for (const key of [
      'activeThreadId', 'selectedArtifactId', 'selectedCaptureId',
    ] as const) {
      if (ui[key] === oldId) ui[key] = newId
    }
    if (draft.capture?.pendingCaptureId === oldId) draft.capture.pendingCaptureId = newId

    for (const m of Object.values(draft.messages ?? {}) as any[]) {
      if (m?.threadId === oldId) m.threadId = newId
    }
    for (const a of Object.values(draft.artifacts ?? {}) as any[]) {
      if (a?.threadId === oldId) a.threadId = newId
      if (a?.messageId === oldId) a.messageId = newId
    }
    for (const c of Object.values(draft.captures ?? {}) as any[]) {
      if (c?.threadId === oldId) c.threadId = newId
    }
  },
}
